import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../utils/cn';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    isLoading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    fullWidth?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            className,
            variant = 'primary',
            size = 'md',
            isLoading = false,
            leftIcon,
            rightIcon,
            fullWidth = false,
            disabled,
            ...props
        },
        ref
    ) => {
        const baseStyles = `
      inline-flex items-center justify-center gap-2
      font-medium rounded-lg
      transition-all duration-200
      focus:outline-none focus:ring-2 focus:ring-offset-2
      disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none
      active:scale-95
    `;

        const variantStyles = {
            primary: `
        bg-primary-500 text-white
        hover:bg-primary-600
        focus:ring-primary-500
        shadow-sm hover:shadow-md
      `,
            secondary: `
        bg-gray-100 text-gray-900
        hover:bg-gray-200
        dark:bg-gray-800 dark:text-gray-100
        dark:hover:bg-gray-700
        focus:ring-gray-500
      `,
            outline: `
        border-2 border-primary-500 text-primary-600
        hover:bg-primary-50
        dark:text-primary-400 dark:border-primary-400
        dark:hover:bg-primary-950
        focus:ring-primary-500
      `,
            ghost: `
        text-gray-700 hover:bg-gray-100
        dark:text-gray-300 dark:hover:bg-gray-800
        focus:ring-gray-500
      `,
            danger: `
        bg-error-500 text-white
        hover:bg-error-600
        focus:ring-error-500
        shadow-sm hover:shadow-md
      `,
        };

        const sizeStyles = {
            xs: 'px-2.5 py-1.5 text-xs',
            sm: 'px-3 py-2 text-sm',
            md: 'px-4 py-2.5 text-sm',
            lg: 'px-5 py-3 text-base',
            xl: 'px-6 py-3.5 text-base',
        };

        const widthStyles = fullWidth ? 'w-full' : '';

        return (
            <button
                ref={ref}
                className={cn(
                    baseStyles,
                    variantStyles[variant],
                    sizeStyles[size],
                    widthStyles,
                    className
                )}
                disabled={disabled || isLoading}
                {...props}
            >
                {isLoading && (
                    <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                    </svg>
                )}
                {!isLoading && leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
                {children}
                {!isLoading && rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
            </button>
        );
    }
);

Button.displayName = 'Button';

export default Button;
